import { revalidateLogic } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import logo from "@/assets/landing/logo-rocortado.png";
import { PhoneInput } from "@/components/form/phone-input";
import { useAppForm } from "@/components/form/tanstack-form";
import { Button } from "@/components/ui/button";
import { useLogout } from "@/hooks/use-logout";
import { parsePocketBaseError } from "@/lib/errors/pocketbase";
import { pb } from "@/lib/pocketbase";
import { phoneNumberSchema } from "@/lib/utils";

const validateSearch = z.object({
	phoneNumber: z.string().optional(),
});

export const Route = createFileRoute("/auth/send-verification")({
	validateSearch,
	beforeLoad: () => {
		if (!pb.authStore.isValid) {
			throw redirect({ to: "/auth/login" });
		}
	},
	component: RouteComponent,
});

const formSchema = z.object({
	phoneNumber: phoneNumberSchema(),
});

type FormValues = z.infer<typeof formSchema>;

function RouteComponent() {
	const router = useRouter();
	const logout = useLogout();
	const { t } = useTranslation();

	const phoneNumber = Route.useSearch({
		select: (search) => search.phoneNumber,
	});

	const sendVerificationCode = useMutation({
		mutationFn: async (data: FormValues) => {
			toast.loading(
				t(($) => $.modules.auth.sendVerification.status.loading.title),
				{
					id: "send-code",
					description: t(($) => $.modules.auth.sendVerification.status.loading.message),
				}
			);

			await pb.send("/api/send-verification-code-sms", {
				method: "POST",
				body: { phoneNumber: data.phoneNumber },
			});

			return data;
		},
		onSuccess: (data) => {
			toast.success(
				t(($) => $.modules.auth.sendVerification.status.success.title),
				{
					id: "send-code",
					description: t(($) => $.modules.auth.sendVerification.status.success.message),
				}
			);

			router.navigate({
				to: "/auth/verify-phone",
				search: { phoneNumber: data.phoneNumber },
			});
		},
		onError: (error) => {
			const parsed = parsePocketBaseError(error, defaultValues);

			if (parsed.unknown) {
				return toast.error(
					t(($) => $.modules.auth.sendVerification.status.error.title),
					{
						id: "send-code",
						description: parsed.message || t(($) => $.modules.auth.sendVerification.status.error.message),
					}
				);
			}

			form.setErrorMap({ onDynamic: parsed.errorMap });
			toast.dismiss("send-code");
		},
	});

	const defaultValues: FormValues = {
		phoneNumber: phoneNumber || "",
	};

	const form = useAppForm({
		defaultValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: { onDynamic: formSchema },
		onSubmit: ({ value }) => {
			sendVerificationCode.mutate(value);
		},
	});

	const handleSubmit = useCallback(
		(e: React.SubmitEvent) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		},
		[form]
	);

	return (
		<div className="mx-auto w-full max-w-md space-y-8">
			<div className="flex flex-col items-center">
				<img alt="Logo" height={"192.667"} src={logo} width={"447.967"} />
				<h2 className="mt-6 text-center font-bold text-3xl text-foreground tracking-tight">
					{t(($) => $.modules.auth.sendVerification.title)}
				</h2>
			</div>

			<form.AppForm>
				<form className="space-y-6" onSubmit={handleSubmit}>
					<form.AppField name="phoneNumber">
						{(field) => (
							<field.Field>
								<field.FieldLabel>{t(($) => $.modules.auth.common.fields.phoneNumber.label)}</field.FieldLabel>
								<field.FieldControl>
									<PhoneInput
										autoComplete="tel"
										onBlur={field.handleBlur}
										onChange={(value) => field.handleChange(value)}
										placeholder={t(($) => $.modules.auth.common.fields.phoneNumber.placeholder)}
										type="tel"
										value={field.state.value}
									/>
								</field.FieldControl>
								<field.FieldError />
							</field.Field>
						)}
					</form.AppField>
				</form>
			</form.AppForm>

			<Button className="mt-6 w-full" disabled={sendVerificationCode.isPending} onClick={() => form.handleSubmit()}>
				{t(($) => $.modules.auth.sendVerification.buttons.submit)}
			</Button>

			<Button className="mt-4 w-full" disabled={logout.isPending} onClick={() => logout.mutate()} variant="secondary">
				{t(($) => $.modules.auth.sendVerification.buttons.logout)}
			</Button>
		</div>
	);
}
